import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	Output
} from '@angular/core';
import { Observable, of, Subject, timer } from 'rxjs';
import { catchError, exhaustMap, takeUntil } from 'rxjs/operators';
import { AffiliateFinderService } from '@brand-lolly/app/social-media-influencer/modules/affiliates/pages/finder/services/affiliate-finder.service';
import {
	FinderResultsResponse,
	FinderResultsSearchState
} from '@brand-lolly/app/social-media-influencer/modules/affiliates/pages/finder/models/finder.model';

/** The other searches' cadence, so every wait in the conversation reads the same. */
const POLL_INTERVAL_MS = 3000;

/** One dropped read is a blip; three in a row is losing sight of the search. */
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

/**
 * How a watched Finder search ended. `finished` is false when the run could not
 * be read at all — the watcher lost it, nothing says it finished.
 */
export interface AnaFinderSearchEnd {
	finished: boolean;
}

/**
 * The offscreen watcher of an Affiliate Finder search Ana started.
 *
 * The sibling of `app-ana-marketplace-search-watch` for a partner search: it
 * draws nothing — the working line in the conversation is the running signal —
 * and polls the run's results until the whole search has settled. The Finder is
 * read through its own service, the read the results page polls, so the two
 * cannot disagree about when the search is over.
 *
 * Inputs: `runId` — the Finder run to watch; null stops the poll.
 * Emits: `searchEnded` once per run, settled or lost.
 */
@Component({
	standalone: true,
	selector: 'app-ana-finder-search-watch',
	template: '',
	styles: [':host { display: none; }']
})
export class AnaFinderSearchWatchComponent implements OnDestroy {
	@Output() searchEnded = new EventEmitter<AnaFinderSearchEnd>();

	private currentRunId: string | null = null;
	private consecutiveFailures = 0;
	private endedReported = false;
	private stopPolling = new Subject<void>();
	private unsubscriber = new Subject<void>();

	constructor(private finderService: AffiliateFinderService) {}

	@Input() set runId(value: string | null | undefined) {
		const nextRunId = value ?? null;
		if (nextRunId === this.currentRunId) return;
		this.currentRunId = nextRunId;
		this.stopPolling.next();
		this.consecutiveFailures = 0;
		this.endedReported = false;
		if (nextRunId) {
			this.startPolling(nextRunId);
		}
	}

	ngOnDestroy(): void {
		this.stopPolling.next();
		this.stopPolling.complete();
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	private startPolling(runId: string): void {
		timer(0, POLL_INTERVAL_MS)
			.pipe(
				// exhaustMap: a slow read is never cancelled and re-issued.
				exhaustMap(() => this.pollOnce(runId)),
				takeUntil(this.stopPolling),
				takeUntil(this.unsubscriber)
			)
			.subscribe(results => this.applyReading(results));
	}

	/** Resolves to null when the read could not be served. */
	private pollOnce(runId: string): Observable<FinderResultsResponse | null> {
		return this.finderService
			.getResults(runId)
			.pipe(catchError(() => of(null)));
	}

	private applyReading(results: FinderResultsResponse | null): void {
		if (!results) {
			this.consecutiveFailures += 1;
			if (this.consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
				this.reportEnded({ finished: false });
			}
			return;
		}
		this.consecutiveFailures = 0;
		if (!this.hasSettled(results)) return;
		this.reportEnded({ finished: true });
	}

	/**
	 * TRAP: `results_ready` alone is NOT the end — the Finder reports it at the
	 * first candidates, while other sources are still collecting. The search is
	 * over when its status has left `searching` AND no source is outstanding, or
	 * the served `settles_at` has passed (or is absent: a run that was mid-pass
	 * has no window, and the results page treats that as closed too).
	 */
	private hasSettled(results: FinderResultsResponse): boolean {
		if (results.search_status === 'searching') return false;
		const state = results.search_state;
		if (!state?.searching_sources?.length) return true;
		return !this.collectionWindowOpen(state);
	}

	private collectionWindowOpen(state: FinderResultsSearchState): boolean {
		if (!state.settles_at) return false;
		const settlesAt = new Date(state.settles_at).getTime();
		return !isNaN(settlesAt) && settlesAt > Date.now();
	}

	/** Once per run: the host starts a conversation turn on it. */
	private reportEnded(end: AnaFinderSearchEnd): void {
		this.stopPolling.next();
		if (this.endedReported) return;
		this.endedReported = true;
		this.searchEnded.emit(end);
	}
}
